/**
 * URL of the snaplore-products portal (Guides). In prod + stage the products app
 * lives on its own dedicated subdomain (`products.<zone>`) — cookie scope
 * `.snaplore.com` / `.testinghub.xyz` keeps auth shared across the legacy and
 * products subdomains, so a plain navigation is a complete session handoff.
 *
 * The React SPA only registers routes under `/products/*`; land on the How-to
 * Home rather than a bare path.
 */
const DEFAULT_PRODUCTS_PATH = '/products/howto/home';

/**
 * A `returnTo` deep-link is only ever honored when it points back into the
 * products SPA itself (M2) — anything else (an absolute URL, a bare `/`, a
 * path into this legacy app) is not a valid products-app destination.
 */
export const isValidProductsReturnTo = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('/products');

export const buildProductsUrl = (hostname: string, target: string = DEFAULT_PRODUCTS_PATH): string => {
  // `acme.testinghub.xyz` → `https://products.testinghub.xyz/products/howto/home`
  // A 2-label apex host (`snaplore.com`) must NOT be stripped down to its TLD
  // (`com`) — only strip the leading label when there is one to spare (H4).
  const parts = hostname.split('.');
  const zone = parts.length > 2 ? parts.slice(1).join('.') : hostname;
  return `https://products.${zone}${target}`;
};

export const getProductsUrl = (target?: string): string => {
  const path = isValidProductsReturnTo(target) ? target : DEFAULT_PRODUCTS_PATH;
  // Override via `VUE_APP_PRODUCTS_URL` in `.env.local` if your products
  // dev server runs on a different port; swap only the path so a validated
  // `returnTo` deep-link still works locally.
  // An override that is not an absolute URL (e.g. the string 'undefined' an
  // unset PRODUCTS_URL becomes in vue.config.js) is ignored, never navigated to:
  // it used to send a paid Guides login to `<app>/undefined` (SNAP-2231).
  if (process.env.VUE_APP_PRODUCTS_URL) {
    try {
      const url = new URL(process.env.VUE_APP_PRODUCTS_URL);
      url.pathname = path;
      return url.toString();
    } catch {
      // fall through to the derived URL
    }
  }
  if (process.env.NODE_ENV !== 'production') return `http://localhost:5173${path}`;
  return buildProductsUrl(window.location.hostname, path);
};

/**
 * Absolute URL back to the legacy Snaplore app.
 *
 * The checkout page runs on `VUE_APP_BILLING_HOSTNAME`, a DIFFERENT origin from
 * the tenant app. There the Vuex store is usually not hydrated, so
 * `company.getDomainAddress()` is unavailable — the old `http://localhost:<port>`
 * fallback then stranded the user on the billing host in stage/prod (SNAP-2231
 * QA: "перейшла на снеплор — там лінк billing.testinghub.xyz"). Prefer the
 * company domain, fall back to the canonical `VUE_APP_URL`, and only use
 * localhost outside production.
 */
export const getSnaploreAppUrl = (companyDomainAddress?: string | null): string => {
  if (companyDomainAddress) return `${companyDomainAddress}/#/`;
  if (process.env.VUE_APP_URL) return `${process.env.VUE_APP_URL}/#/`;
  return `http://localhost:${process.env.VUE_APP_PORT}/#/`;
};
